import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, User } from "lucide-react";

import { ApiError, api } from "../../lib/api";
import { authStorage } from "../../lib/auth";

import styles from "./LoginPage.module.css";

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [forceChange, setForceChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!username || !password) {
      setError("Preenche o nome de utilizador e a palavra-passe.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.login(username, password);
      authStorage.setSession(result.access, result.refresh, result.user);
      if (result.force_password_change) {
        setForceChange(true);
        return;
      }
      navigate("/casos", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { attempts_remaining?: number } | null;
        const remaining = body?.attempts_remaining;
        if (typeof remaining === "number") {
          setError(
            `Credenciais inválidas. Tentativas restantes: ${remaining}.`,
          );
        } else if (err.status === 403) {
          setError("Conta bloqueada. Contacta um administrador.");
        } else {
          setError(err.message || "Não foi possível iniciar sessão.");
        }
      } else {
        setError("Não foi possível ligar ao servidor.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleChangePassword(event: FormEvent) {
    event.preventDefault();
    setChangeError(null);

    if (newPassword.length < 8) {
      setChangeError("A nova palavra-passe deve ter pelo menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError("As palavras-passe não coincidem.");
      return;
    }

    setChangingPassword(true);
    try {
      await api.changePassword(newPassword);
      navigate("/casos", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setChangeError(err.message || "Não foi possível alterar a palavra-passe.");
      } else {
        setChangeError("Não foi possível ligar ao servidor.");
      }
    } finally {
      setChangingPassword(false);
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <div className={styles.logoWrap}>
          <img
            src="/logo-apc.png"
            alt="ApClima"
            className={styles.logoApc}
            width={220}
            height={220}
          />
        </div>
        <h1 className={styles.heading}>Bem-vindo de volta!</h1>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <User size={20} className={styles.fieldIcon} aria-hidden="true" />
            <input
              type="text"
              className={styles.input}
              placeholder="Nome de utilizador"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              aria-label="Nome de utilizador"
            />
          </div>

          <div className={styles.field}>
            <Lock size={20} className={styles.fieldIcon} aria-hidden="true" />
            <input
              type={showPassword ? "text" : "password"}
              className={styles.input}
              placeholder="Palavra-passe"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              aria-label="Palavra-passe"
            />
            <button
              type="button"
              className={styles.fieldEye}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <button type="submit" className={styles.submit} disabled={submitting}>
            {submitting ? "A iniciar sessão…" : "Iniciar sessão"}
          </button>
{/*
          <a className={styles.forgot} href="#esqueci">
            Esqueceste-te da tua palavra-passe?
          </a> */}
        </form>
      </section>

      {forceChange && (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="force-change-title">
          <section className={styles.modalCard}>
            <h2 id="force-change-title" className={styles.modalHeading}>
              Altera a tua palavra-passe
            </h2>
            <p className={styles.modalDesc}>
              Por segurança, deves alterar a palavra-passe provisória antes de continuar.
            </p>

            <form className={styles.form} onSubmit={handleChangePassword} noValidate>
              <div className={styles.field}>
                <Lock size={20} className={styles.fieldIcon} aria-hidden="true" />
                <input
                  type={showNew ? "text" : "password"}
                  className={styles.input}
                  placeholder="Nova palavra-passe"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={changingPassword}
                  aria-label="Nova palavra-passe"
                />
                <button
                  type="button"
                  className={styles.fieldEye}
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className={styles.field}>
                <Lock size={20} className={styles.fieldIcon} aria-hidden="true" />
                <input
                  type={showConfirm ? "text" : "password"}
                  className={styles.input}
                  placeholder="Confirmar palavra-passe"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={changingPassword}
                  aria-label="Confirmar palavra-passe"
                />
                <button
                  type="button"
                  className={styles.fieldEye}
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {changeError && (
                <div className={styles.error} role="alert">
                  {changeError}
                </div>
              )}

              <button type="submit" className={styles.submit} disabled={changingPassword}>
                {changingPassword ? "A guardar…" : "Guardar nova palavra-passe"}
              </button>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
